import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import Information from "./_information";
import { formatDateTimeAutoToString } from "@/utils/format";
import moment from "moment-timezone";
import { useEditHouseRentalMutation, useGetHouseRentalQuery } from "@/services/house-rental.service";
import { HouseRentalForm } from "@/types/house-rental.type";

export default function EditHouseRental() {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const {state: {id, business_unit_id}} = location
    const [editRental, {isSuccess}] = useEditHouseRentalMutation()
    const {data: houseRental} = useGetHouseRentalQuery(id || "", {refetchOnMountOrArgChange: true})
    const methods = useForm<HouseRentalForm>()
    const {handleSubmit, setValue} = methods

    const onSubmit = handleSubmit(async (data) => {
        if(houseRental) {
            let taStart = houseRental.ta_start
            let taEnd = houseRental.ta_end
            let takeOver = houseRental.take_over
            let now = moment();
            if (formatDateTimeAutoToString(houseRental.ta_start, "YYYY-MM-DD") !== data.ta_start) {
                taStart = moment(`${data.ta_start} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            if (formatDateTimeAutoToString(houseRental.ta_end, "YYYY-MM-DD") !== data.ta_end) {
                taEnd = moment(`${data.ta_end} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            if (formatDateTimeAutoToString(houseRental.take_over, "YYYY-MM-DD") !== data.take_over) {
                takeOver = moment(`${data.take_over} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            editRental({
                ...{
                    ...data,
                    ta_start: taStart,
                    ta_end: taEnd,
                    take_over: takeOver,
                    rent: Number(data.rent),
                    ta_duration: Number(data.ta_duration),
                    deposit_amount: Number(data.deposit_amount),
                    warranty: _.isEmpty(data.warranty) ? -1 : Number(data.warranty),
                    rent_free: _.isEmpty(data.rent_free) ? -1 : Number(data.rent_free),
                    option_to_renew: !_.isEmpty(data.option_to_renew) ? data.option_to_renew : null,
                }, 
                id: houseRental?.id,
            })
        }
    })

    useEffect(() => {
        if (houseRental) {
            setValue("business_unit_id", business_unit_id ? business_unit_id : houseRental.business_unit_id)
            setValue("rent", houseRental.rent)
            houseRental.rent_free >= 0 && setValue("rent_free", houseRental.rent_free)
            setValue("take_over", formatDateTimeAutoToString(houseRental.take_over, "YYYY-MM-DD"))
            setValue("deposit_amount", houseRental.deposit_amount)
            setValue("ta_start", formatDateTimeAutoToString(houseRental.ta_start, "YYYY-MM-DD"))
            setValue("ta_duration", houseRental.ta_duration)
            setValue("ta_end", formatDateTimeAutoToString(houseRental.ta_end, "YYYY-MM-DD"))
            houseRental.warranty >= 0 && setValue("warranty", houseRental.warranty)
            setValue("option_to_renew", houseRental.option_to_renew)
            setValue("agent_name", houseRental.agent_name)
        }
    }, [houseRental])

    useEffect(() => {
        if(isSuccess) {
            navigate(location, {state: {reload: true}})
        }
    }, [isSuccess])

    return (
        <div x-show="modalOpen" x-transition="" className="fixed top-0 left-0 z-99999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
            <div className="w-full max-w-screen-lg rounded-lg bg-white py-5 px-8 dark:bg-boxdark">
                <h3 className="text-xl dark:text-white text-center font-medium my-5">
                    {t("house_rental.edit_house_rental")}
                </h3>
                <form className="mx-5" onSubmit={onSubmit}>
                    <FormProvider {...methods}>
                        <Information />
                    </FormProvider>
                    <div className="flex justify-end">
                        <div className="px-3">
                            <button type="button" onClick={() => navigate(location)} className="block w-full rounded border border-stroke bg-gray-400 p-1.5 px-3 text-center font-medium text-black transition hover:bg-opacity-90">
                                {t("modal.cancel")}
                            </button>
                        </div>
                        <div className="px-3">
                            <button type="submit" className="flex w-full justify-center rounded bg-primary p-1.5 px-3 text-white hover:bg-opacity-90">
                                {t("modal.submit")}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
